import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { AnimatePresence } from 'framer-motion';

// Components
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Projects from './components/Projects';
import Skills from './components/Skills';
import Contact from './components/Contact';
import CustomCursor from './components/CustomCursor';
import WebGLBackground from './components/WebGLBackground';
import ParticleBackground from './components/ParticleBackground';
import LoadingScreen from './components/LoadingScreen';
import TransitionLayout from './components/TransitionLayout';

// Hooks
import useSound from './components/useSound';
import useSmoothScroll from './components/useSmoothScroll';

// Themes and Styles
import { lightTheme, darkTheme } from './styles/themes';
import GlobalStyles from './styles/GlobalStyles';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setTheme] = useState('dark');
  const { playHover, playClick } = useSound();
  useSmoothScroll();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
    playClick();
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
      <Router>
        <AppContainer>
          <GlobalStyles />
          <CustomCursor />
          <WebGLBackground />
          <ParticleBackground />
          
          <MainContent>
            <Navbar toggleTheme={toggleTheme} currentTheme={theme} />
            
            <TransitionLayout>
              <AnimatePresence mode='wait'>
                <Routes>
                  <Route 
                    path="/" 
                    element={<Hero playHover={playHover} playClick={playClick} />} 
                  />
                  <Route 
                    path="/about" 
                    element={<About playHover={playHover} playClick={playClick} />} 
                  />
                  <Route 
                    path="/projects" 
                    element={<Projects playHover={playHover} playClick={playClick} />} 
                  />
                  <Route 
                    path="/skills" 
                    element={<Skills playHover={playHover} playClick={playClick} />} 
                  />
                  <Route 
                    path="/contact" 
                    element={<Contact playHover={playHover} playClick={playClick} />} 
                  />
                </Routes>
              </AnimatePresence>
            </TransitionLayout>

            <SocialLinks>
              <SocialLink 
                href="https://github.com/yourusername" 
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={playHover}
                onClick={playClick}
              >
                GH
              </SocialLink>
              <SocialLink 
                href="https://linkedin.com/in/yourusername" 
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={playHover}
                onClick={playClick}
              >
                LI
              </SocialLink>
              <SocialLink 
                href="https://twitter.com/yourusername" 
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={playHover}
                onClick={playClick}
              >
                TW
              </SocialLink>
            </SocialLinks>

            <EmailLink 
              href="mailto:your.email@example.com"
              onMouseEnter={playHover}
              onClick={playClick}
            >
              your.email@example.com
            </EmailLink>
          </MainContent>
        </AppContainer>
      </Router>
    </ThemeProvider>
  );
}

const AppContainer = styled.div`
  position: relative;
  min-height: 100vh;
  overflow: hidden;
`;

const MainContent = styled.main`
  position: relative;
  z-index: 1;
`;

const SocialLinks = styled.div`
  position: fixed;
  bottom: 0;
  left: 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  
  &:after {
    content: '';
    width: 1px;
    height: 90px;
    background-color: ${props => props.theme.text};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const SocialLink = styled.a`
  color: ${props => props.theme.text};
  text-decoration: none;
  font-size: 14px;
  letter-spacing: 1px;
  writing-mode: vertical-rl;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.accent};
    transform: translateY(-3px);
  }
`;

const EmailLink = styled.a`
  position: fixed;
  bottom: 0;
  right: 40px;
  writing-mode: vertical-rl;
  color: ${props => props.theme.text};
  text-decoration: none;
  font-size: 14px;
  letter-spacing: 1px;
  padding: 20px;

  &:after {
    content: '';
    display: block;
    width: 1px;
    height: 90px;
    margin: 0 auto;
    background-color: ${props => props.theme.text};
  }

  &:hover {
    color: ${props => props.theme.accent};
    transform: translateY(-3px);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export default App;